/* eslint-disable @typescript-eslint/ban-ts-comment, @typescript-eslint/no-unused-vars */
// @ts-nocheck
import QRCode from "qrcode";

import {
  base64UrlEncode,
  canonicalJson,
  createWalletLinkBrowserParticipant,
  createWalletLinkProofHeaders,
  decryptWalletLinkEnvelope,
  encryptWalletLinkEnvelope,
  randomBase64Url,
  sha256Base64Url,
  verifyWalletLinkAttestation,
  walletLinkPublicKeyFingerprint,
  WALLET_LINK_DAPP_ID,
  WALLET_LINK_PROTOCOL_VERSION
} from "./wallet-link-crypto";
import {
  decryptWalletLinkAuthorizationVerifier,
  loadWalletLinkAuthorization,
  loadWalletLinkSession,
  removeWalletLinkAuthorization,
  removeWalletLinkSession,
  saveWalletLinkAuthorization,
  saveWalletLinkSession,
  storedWalletLinkAuthorizationParticipant,
  storedWalletLinkParticipant
} from "./wallet-link-store";

const SYNERGY_TESTNET = Object.freeze({ chainIdDecimal: 1266, chainIdHex: "0x4f2" });
function chainIdToDecimal(value) {
  if (typeof value === "number" && Number.isFinite(value)) return Math.trunc(value);
  const text = String(value ?? "").trim().toLowerCase();
  if (!text) return null;
  const parsed = Number.parseInt(text, text.startsWith("0x") ? 16 : 10);
  return Number.isFinite(parsed) ? parsed : null;
}
function isCurrentTestnetChain(value) { return chainIdToDecimal(value) === SYNERGY_TESTNET.chainIdDecimal; }

export const KEYSTONE_WALLET_LINK_ORIGIN = "https://horizon.synergy-network.io";
export const KEYSTONE_WALLET_LINK_REDIRECT_URI = `${KEYSTONE_WALLET_LINK_ORIGIN}/wallet/callback`;
export const WALLET_LINK_AUTHORITY = "https://connect.wallet.synergy-network.io";
export const WALLET_LINK_API_BASE = `${WALLET_LINK_AUTHORITY}/v1`;
export const WALLET_WEB_HUB_ORIGIN = "https://wallet.synergy-network.io";
export const KEYSTONE_WALLET_LINK_SCOPES = Object.freeze([
  "read:identity",
  "read:address",
  "sign:message",
  "wallet:getCapabilities"
]);
export const KEYSTONE_WALLET_LINK_METHODS = Object.freeze([
  "synergy_requestAccounts",
  "synergy_accounts",
  "synergy_chainId",
  "wallet_getCapabilities",
  "synergy_signMessage",
  "wallet_disconnect"
]);
export const KEYSTONE_WALLET_LINK_EVENTS = Object.freeze([
  "connect",
  "disconnect",
  "accountsChanged",
  "chainChanged",
  "sessionExpiring",
  "sessionRevoked",
  "requestUpdated"
]);

const SESSION_DURATION_SECONDS = 3_600;
const MAX_JSON_RESPONSE_BYTES = 64 * 1024;
const AUTHORIZATION_ID_PATTERN = /^wha_[A-Za-z0-9_-]{43}\.[A-Za-z0-9_-]{43}$/;
const AUTHORIZATION_CODE_PATTERN = /^whc_[A-Za-z0-9_-]{43}\.[A-Za-z0-9_-]{43}\.[A-Za-z0-9_-]{43}$/;
const STATE_PATTERN = /^[A-Za-z0-9_-]{43,128}$/;
const SESSION_ID_PATTERN = /^wls_[A-Za-z0-9_-]{43}\.[A-Za-z0-9_-]{43}$/;
const CAPABILITY_PATTERN = /^wlc_[A-Za-z0-9_-]{43}\.[A-Za-z0-9_-]{43}$/;
const ACTION_ID_PATTERN = /^wlr_[A-Za-z0-9_-]{32}$/;

function walletLinkChainIdToDecimal(value) {
  const text = String(value ?? "").trim().toLowerCase();
  if (text === "synergy:1266" || text === "synergy-testnet-v3") return SYNERGY_TESTNET.chainIdDecimal;
  return chainIdToDecimal(value);
}

function isWalletLinkTestnet(value) {
  return walletLinkChainIdToDecimal(value) === SYNERGY_TESTNET.chainIdDecimal;
}

function assertProductionOrigin() {
  if (typeof window !== "undefined" && window.location.origin !== KEYSTONE_WALLET_LINK_ORIGIN) {
    throw new Error(`Wallet Link is restricted to the registered Horizon origin ${KEYSTONE_WALLET_LINK_ORIGIN}.`);
  }
}

function walletLinkUrl(path) {
  if (!/^\/v1\/[A-Za-z0-9_./-]+$/.test(path) || path.includes("..")) {
    throw new Error("Wallet Link refused a non-canonical service path.");
  }
  return `${WALLET_LINK_AUTHORITY}${path}`;
}

function sessionPath(sessionId) {
  if (!SESSION_ID_PATTERN.test(sessionId)) throw new Error("Wallet Link session ID is invalid.");
  return `/v1/sessions/${encodeURIComponent(sessionId)}`;
}

function errorMessage(payload, response) {
  return String(
    payload?.error?.message || payload?.message || `Synergy Wallet Link returned HTTP ${response.status || "error"}.`
  )
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 320);
}

export async function parseWalletLinkJson(response) {
  const contentLength = Number(response.headers.get("content-length") || 0);
  if (Number.isFinite(contentLength) && contentLength > MAX_JSON_RESPONSE_BYTES) {
    throw new Error("Synergy Wallet Link returned an oversized response.");
  }
  const contentType = String(response.headers.get("content-type") || "").toLowerCase();
  if (!/^application\/(?:[a-z0-9.+-]*\+)?json(?:\s*;|$)/i.test(contentType)) {
    throw new Error(`Synergy Wallet Link returned ${contentType || "a non-JSON response"} instead of JSON.`);
  }
  const text = await response.text();
  if (new TextEncoder().encode(text).length > MAX_JSON_RESPONSE_BYTES) {
    throw new Error("Synergy Wallet Link returned an oversized response.");
  }
  let payload;
  try {
    payload = text ? JSON.parse(text) : {};
  } catch {
    throw new Error("Synergy Wallet Link returned malformed JSON.");
  }
  if (!response.ok) throw new Error(errorMessage(payload, response));
  return payload;
}

async function walletLinkRequest({ participant, sessionId = null, path, method = "GET", body = null, attestation = null, fetchImpl = fetch }) {
  const bodyText = body === null || body === undefined ? "" : canonicalJson(body);
  const headers = await createWalletLinkProofHeaders({
    participant,
    origin: KEYSTONE_WALLET_LINK_ORIGIN,
    method,
    path,
    bodyText,
    sessionId
  });
  if (attestation) {
    headers.set("X-Synergy-DApp-Attestation", base64UrlEncode(new TextEncoder().encode(canonicalJson(attestation))));
  }
  const response = await fetchImpl(walletLinkUrl(path), {
    method,
    headers,
    body: bodyText || undefined,
    mode: "cors",
    credentials: "omit",
    cache: "no-store",
    redirect: "error",
    referrerPolicy: "no-referrer"
  });
  return parseWalletLinkJson(response);
}

async function publicWalletLinkRequest(path, fetchImpl = fetch) {
  const response = await fetchImpl(walletLinkUrl(path), {
    method: "GET",
    mode: "cors",
    credentials: "omit",
    cache: "no-store",
    redirect: "error",
    referrerPolicy: "no-referrer",
    headers: { Accept: "application/json" }
  });
  return parseWalletLinkJson(response);
}

function exactStringSet(actual, expected) {
  return Array.isArray(actual) && actual.length === expected.length && expected.every((value) => new Set(actual.map(String)).has(value));
}

function assertExactStringSet(actual, expected, label) {
  if (!exactStringSet(actual, expected)) throw new Error(`Synergy Wallet Link changed the Horizon ${label} grant.`);
}

function requireString(value, label) {
  const text = String(value || "").trim();
  if (!text) throw new Error(`Synergy Wallet Link returned an invalid ${label}.`);
  return text;
}

function requireFutureExpiry(value) {
  const expiresAt = requireString(value, "expiration");
  const time = Date.parse(expiresAt);
  if (!Number.isFinite(time) || time <= Date.now()) throw new Error("Synergy Wallet Link returned an expired request.");
  return new Date(time).toISOString();
}

function validateHubAuthorizeUrl(value, authorizationRequestId) {
  const url = new URL(String(value || ""));
  if (
    url.origin !== WALLET_WEB_HUB_ORIGIN ||
    url.pathname !== "/connect/authorize" ||
    url.username ||
    url.password ||
    url.hash ||
    [...url.searchParams.keys()].some((key) => key !== "authorizationRequestId") ||
    url.searchParams.getAll("authorizationRequestId").length !== 1 ||
    url.searchParams.get("authorizationRequestId") !== authorizationRequestId
  ) throw new Error("Wallet Web Hub returned an invalid authorization URL.");
  return url.href;
}

function extractAccount(value) {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return extractAccount(value[0]);
  return String(
    value?.synergyAddress || value?.walletAddress || value?.account || value?.address ||
    value?.accountsByChain?.[SYNERGY_TESTNET.chainIdHex] ||
    value?.accountsByChain?.[SYNERGY_TESTNET.chainIdDecimal] ||
    value?.session?.account || value?.session?.address || value?.wallet?.address || ""
  );
}

function extractSynId(value) {
  return value?.synId || value?.syn_id || value?.session?.synId || value?.wallet?.synId || "";
}

function extractChainId(value) {
  return value?.chainId ?? value?.chain_id ?? value?.network?.chainId ?? value?.session?.chainId ?? null;
}

function providerRequest(provider, method, params = []) {
  if (!provider) throw new Error("Synergy Wallet provider is not available.");
  if (typeof provider.request === "function") return provider.request({ method, params });
  if (typeof provider.send === "function") return provider.send(method, params);
  throw new Error("Synergy Wallet provider does not expose a request method.");
}

export function getInjectedSynergyProvider() {
  if (typeof window === "undefined") return null;
  return window.synergy || window.SynergyWallet || window.synergyWallet || null;
}

export function providerCanSign(provider = getInjectedSynergyProvider()) {
  return Boolean(provider && (
    typeof provider.signTransaction === "function" ||
    typeof provider.sendTransaction === "function" ||
    typeof provider.request === "function"
  ));
}

async function requestOptional(provider, methods) {
  for (const method of methods) {
    try {
      if (method.type === "fn" && typeof provider?.[method.name] === "function") {
        return await provider[method.name](...(method.params || []));
      }
      if (method.type === "request") return await providerRequest(provider, method.name, method.params || []);
    } catch {
      // Continue through supported injected-provider compatibility methods.
    }
  }
  return null;
}

export async function getConnectedSynergyWallet(provider = getInjectedSynergyProvider()) {
  if (!provider) return null;
  const accountsResult = await requestOptional(provider, [
    { type: "request", name: "synergy_accounts" },
    { type: "request", name: "synergy_requestAccounts", params: [{ silent: true }] },
    { type: "request", name: "eth_accounts" }
  ]);
  const address = extractAccount(accountsResult);
  if (!address) return null;
  const chainResult = await requestOptional(provider, [
    { type: "fn", name: "getChainId" },
    { type: "request", name: "synergy_chainId" },
    { type: "request", name: "eth_chainId" }
  ]);
  return {
    address,
    chainId: chainIdToDecimal(chainResult),
    chainIdHex: isCurrentTestnetChain(chainResult) ? SYNERGY_TESTNET.chainIdHex : null,
    synId: extractSynId(accountsResult),
    source: "injected",
    canSign: providerCanSign(provider),
    provider
  };
}

export async function connectInjectedWallet(provider = getInjectedSynergyProvider()) {
  if (!provider) throw new Error("Install or unlock Synergy Wallet to connect an in-browser wallet provider.");
  let accountsResult;
  try {
    accountsResult = typeof provider.requestAccounts === "function"
      ? await provider.requestAccounts()
      : await providerRequest(provider, "synergy_requestAccounts");
  } catch {
    accountsResult = await providerRequest(provider, "synergy_accounts");
  }
  const address = extractAccount(accountsResult);
  if (!address) throw new Error("Synergy Wallet did not return a wallet address.");
  let chainResult;
  try {
    chainResult = typeof provider.getChainId === "function"
      ? await provider.getChainId()
      : await providerRequest(provider, "synergy_chainId");
  } catch {
    chainResult = await requestOptional(provider, [{ type: "request", name: "eth_chainId" }]);
  }
  if (!isCurrentTestnetChain(chainResult)) {
    throw new Error(`Synergy Wallet is on chain ${chainResult || "unknown"}; Keystone requires ${SYNERGY_TESTNET.chainIdHex}.`);
  }
  return {
    address,
    chainId: chainIdToDecimal(chainResult),
    chainIdHex: SYNERGY_TESTNET.chainIdHex,
    synId: extractSynId(accountsResult),
    source: "injected",
    canSign: true,
    provider
  };
}

export function normalizeSignedEnvelope(result) {
  if (!result) return null;
  if (result.signature || result.signerPublicKey || result.signedBy) return result;
  if (result.signedEnvelope) return normalizeSignedEnvelope(result.signedEnvelope);
  if (result.transaction) return normalizeSignedEnvelope(result.transaction);
  if (result.envelope) return normalizeSignedEnvelope(result.envelope);
  if (result.rawTransaction || result.signedTransaction) return result;
  return null;
}

export async function signEnvelope(envelope, provider = getInjectedSynergyProvider()) {
  if (!provider) throw new Error("Synergy Wallet provider is required for transaction signing.");
  const result = typeof provider.signTransaction === "function"
    ? await provider.signTransaction(envelope)
    : await providerRequest(provider, "synergy_signTransaction", [envelope]);
  const signed = normalizeSignedEnvelope(result);
  if (!signed) throw new Error("Synergy Wallet did not return a signed transaction envelope.");
  return signed;
}

export async function sendEnvelopeViaWallet(envelope, provider = getInjectedSynergyProvider()) {
  if (!provider) throw new Error("Synergy Wallet provider is required to send a transaction.");
  if (typeof provider.sendTransaction === "function") return provider.sendTransaction(envelope);
  return providerRequest(provider, "synergy_sendTransaction", [envelope]);
}

export function getWalletPairingConfig() {
  return {
    ok: true,
    protocolVersion: WALLET_LINK_PROTOCOL_VERSION,
    dappId: WALLET_LINK_DAPP_ID,
    authority: WALLET_LINK_AUTHORITY,
    apiBase: WALLET_LINK_API_BASE,
    webHubOrigin: WALLET_WEB_HUB_ORIGIN,
    origin: KEYSTONE_WALLET_LINK_ORIGIN,
    redirectUri: KEYSTONE_WALLET_LINK_REDIRECT_URI
  };
}

function storedSession(session) {
  return {
    recordVersion: 1,
    protocolVersion: 2,
    dappId: WALLET_LINK_DAPP_ID,
    origin: session.origin,
    redirectUri: session.redirectUri,
    sessionId: session.sessionId,
    topic: session.topic,
    nonce: session.nonce,
    state: session.state,
    codeChallenge: session.codeChallenge,
    signingPublicKey: session.participant.signingPublicKey,
    signingPrivateKey: session.participant.signingPrivateKey,
    transportPublicKey: session.participant.transportPublicKey,
    transportPrivateKey: session.participant.transportPrivateKey,
    walletPublicKey: session.walletPublicKey,
    walletTransportPublicKey: session.walletTransportPublicKey,
    expiresAt: session.expiresAt,
    requestedScopes: [...session.requestedScopes],
    requestedChains: [...session.requestedChains],
    requestedMethods: [...session.requestedMethods],
    requestedEvents: [...session.requestedEvents],
    requestedDurationSeconds: session.requestedDurationSeconds
  };
}

export async function startWalletLinkAuthorization({ fetchImpl, participant, state, codeVerifier } = {}) {
  assertProductionOrigin();
  const browserParticipant = participant || await createWalletLinkBrowserParticipant();
  const requestState = state || randomBase64Url(32);
  const verifier = codeVerifier || randomBase64Url(32);
  if (!STATE_PATTERN.test(requestState)) throw new Error("Wallet Link state is invalid.");
  if (!/^[A-Za-z0-9_-]{43,128}$/.test(verifier)) throw new Error("Wallet Link PKCE verifier is invalid.");
  const proposal = {
    protocolVersion: WALLET_LINK_PROTOCOL_VERSION,
    dappId: WALLET_LINK_DAPP_ID,
    origin: KEYSTONE_WALLET_LINK_ORIGIN,
    redirectUri: KEYSTONE_WALLET_LINK_REDIRECT_URI,
    browserPublicKey: browserParticipant.signingPublicKey,
    browserTransportPublicKey: browserParticipant.transportPublicKey,
    state: requestState,
    codeChallenge: await sha256Base64Url(verifier),
    codeChallengeMethod: "S256",
    requestedScopes: [...KEYSTONE_WALLET_LINK_SCOPES],
    requestedChains: ["synergy:1266"],
    requestedMethods: [...KEYSTONE_WALLET_LINK_METHODS],
    requestedEvents: [...KEYSTONE_WALLET_LINK_EVENTS],
    requestedDurationSeconds: SESSION_DURATION_SECONDS
  };
  const proposalBodyText = canonicalJson(proposal);
  const payload = await walletLinkRequest({
    participant: browserParticipant,
    path: "/v1/web-hub/authorization-requests",
    method: "POST",
    body: proposal,
    fetchImpl
  });
  const authorizationRequestId = String(payload?.authorizationRequestId || "");
  if (!AUTHORIZATION_ID_PATTERN.test(authorizationRequestId) || payload?.status !== "awaiting_hub_authorization") {
    throw new Error("Wallet Web Hub returned an invalid authorization request.");
  }
  return {
    authorizationRequestId,
    hubAuthorizeUrl: validateHubAuthorizeUrl(payload.hubAuthorizeUrl, authorizationRequestId),
    expiresAt: requireFutureExpiry(payload.expiresAt),
    participant: browserParticipant,
    proposal,
    proposalBodyText,
    codeVerifier: verifier
  };
}

export async function persistWalletLinkAuthorization(flow, persist = saveWalletLinkAuthorization) {
  await persist({
    authorizationRequestId: flow.authorizationRequestId,
    hubAuthorizeUrl: flow.hubAuthorizeUrl,
    expiresAt: flow.expiresAt,
    proposal: flow.proposal,
    proposalBodyText: flow.proposalBodyText,
    participant: flow.participant,
    codeVerifier: flow.codeVerifier
  });
}

function restoredAuthorizationFlow(record, codeVerifier) {
  const proposal = record.proposal;
  const participant = storedWalletLinkAuthorizationParticipant(record);
  if (
    !AUTHORIZATION_ID_PATTERN.test(record.authorizationRequestId) ||
    Date.parse(record.expiresAt) <= Date.now() ||
    proposal.protocolVersion !== WALLET_LINK_PROTOCOL_VERSION ||
    proposal.dappId !== WALLET_LINK_DAPP_ID ||
    proposal.origin !== KEYSTONE_WALLET_LINK_ORIGIN ||
    proposal.redirectUri !== KEYSTONE_WALLET_LINK_REDIRECT_URI ||
    proposal.browserPublicKey !== participant.signingPublicKey ||
    proposal.browserTransportPublicKey !== participant.transportPublicKey ||
    proposal.codeChallengeMethod !== "S256" ||
    record.proposalBodyText !== canonicalJson(proposal) ||
    participant.signingPrivateKey.extractable ||
    participant.transportPrivateKey.extractable
  ) throw new Error("Stored Wallet Web Hub authorization is not bound to Horizon.");
  assertExactStringSet(proposal.requestedScopes, KEYSTONE_WALLET_LINK_SCOPES, "scope");
  assertExactStringSet(proposal.requestedChains, ["synergy:1266"], "chain");
  assertExactStringSet(proposal.requestedMethods, KEYSTONE_WALLET_LINK_METHODS, "method");
  assertExactStringSet(proposal.requestedEvents, KEYSTONE_WALLET_LINK_EVENTS, "event");
  return {
    authorizationRequestId: record.authorizationRequestId,
    hubAuthorizeUrl: validateHubAuthorizeUrl(record.hubAuthorizeUrl, record.authorizationRequestId),
    expiresAt: record.expiresAt,
    participant,
    proposal,
    proposalBodyText: record.proposalBodyText,
    codeVerifier
  };
}

function assertCallback(callback, flow) {
  if (!AUTHORIZATION_CODE_PATTERN.test(callback.code)) throw new Error("Wallet Web Hub callback code is invalid.");
  if (callback.state.length !== flow.proposal.state.length || callback.state !== flow.proposal.state) {
    throw new Error("Wallet Web Hub callback state did not match Horizon.");
  }
  if (Date.parse(flow.expiresAt) <= Date.now()) throw new Error("Wallet Web Hub authorization expired.");
}

function buildWalletPairingDeepLink(qr) {
  const url = new URL("synergywallet://connect");
  const entries = {
    protocolVersion: String(qr.protocolVersion),
    dappId: qr.dappId,
    origin: qr.origin,
    redirectUri: qr.redirectUri,
    name: qr.displayName,
    publicSessionId: qr.publicSessionId,
    walletCapability: qr.walletCapability,
    topic: qr.topic,
    nonce: qr.nonce,
    expiresAt: qr.expiresAt,
    dappPublicKey: qr.dappPublicKey,
    dappTransportPublicKey: qr.dappTransportPublicKey,
    state: qr.state,
    codeChallenge: qr.codeChallenge,
    codeChallengeMethod: qr.codeChallengeMethod,
    scopes: qr.requestedScopes.join(","),
    chains: qr.requestedChains.join(","),
    methods: qr.requestedMethods.join(","),
    events: qr.requestedEvents.join(","),
    requestedDurationSeconds: String(qr.requestedDurationSeconds)
  };
  for (const [key, value] of Object.entries(entries)) url.searchParams.set(key, value);
  return url.href;
}

export async function completeWalletLinkAuthorization(flow, callback, { fetchImpl, persistSession = saveWalletLinkSession } = {}) {
  assertProductionOrigin();
  assertCallback(callback, flow);
  const exchanged = await walletLinkRequest({
    participant: flow.participant,
    sessionId: flow.authorizationRequestId,
    path: "/v1/web-hub/attestation-exchange",
    method: "POST",
    body: { authorizationCode: callback.code, codeVerifier: flow.codeVerifier },
    fetchImpl
  });
  if (
    exchanged?.authorizationRequestId !== flow.authorizationRequestId ||
    exchanged?.status !== "exchanged" ||
    !exchanged?.attestation
  ) throw new Error("Wallet Web Hub did not return a bound dApp attestation.");
  const keySet = await publicWalletLinkRequest("/v1/web-hub/attestation-keys", fetchImpl);
  await verifyWalletLinkAttestation({
    attestation: exchanged.attestation,
    keySet,
    proposal: flow.proposal,
    proposalBodyText: flow.proposalBodyText
  });
  const created = await walletLinkRequest({
    participant: flow.participant,
    path: "/v1/sessions",
    method: "POST",
    body: flow.proposal,
    attestation: exchanged.attestation,
    fetchImpl
  });
  const sessionId = String(created?.publicSessionId || created?.sessionId || "");
  const walletCapability = String(created?.walletCapability || created?.walletBootstrap?.capability || "");
  if (!SESSION_ID_PATTERN.test(sessionId) || !CAPABILITY_PATTERN.test(walletCapability)) {
    throw new Error("Synergy Wallet Link returned invalid session capabilities.");
  }
  if (
    Number(created?.protocolVersion) !== WALLET_LINK_PROTOCOL_VERSION ||
    created?.dappId !== WALLET_LINK_DAPP_ID ||
    created?.origin !== KEYSTONE_WALLET_LINK_ORIGIN ||
    created?.redirectUri !== KEYSTONE_WALLET_LINK_REDIRECT_URI ||
    created?.status !== "pending"
  ) throw new Error("Synergy Wallet Link created a session outside the Horizon dApp context.");
  assertExactStringSet(created.requestedScopes, flow.proposal.requestedScopes, "scope");
  assertExactStringSet(created.requestedChains, flow.proposal.requestedChains, "chain");
  assertExactStringSet(created.requestedMethods, flow.proposal.requestedMethods, "method");
  assertExactStringSet(created.requestedEvents, flow.proposal.requestedEvents, "event");
  const expiresAt = requireFutureExpiry(created.expiresAt);
  if (created.pollUrl !== walletLinkUrl(sessionPath(sessionId))) {
    throw new Error("Synergy Wallet Link returned a poll URL outside the fixed Wallet authority.");
  }
  const session = {
    protocolVersion: WALLET_LINK_PROTOCOL_VERSION,
    dappId: WALLET_LINK_DAPP_ID,
    origin: KEYSTONE_WALLET_LINK_ORIGIN,
    redirectUri: KEYSTONE_WALLET_LINK_REDIRECT_URI,
    sessionId,
    topic: requireString(created.topic, "session topic"),
    nonce: requireString(created.nonce, "session nonce"),
    state: flow.proposal.state,
    codeChallenge: flow.proposal.codeChallenge,
    participant: flow.participant,
    expiresAt,
    requestedScopes: [...flow.proposal.requestedScopes],
    requestedChains: [...flow.proposal.requestedChains],
    requestedMethods: [...flow.proposal.requestedMethods],
    requestedEvents: [...flow.proposal.requestedEvents],
    requestedDurationSeconds: flow.proposal.requestedDurationSeconds,
    walletPublicKey: null,
    walletTransportPublicKey: null
  };
  try {
    await persistSession(storedSession(session));
  } catch (error) {
    await walletLinkRequest({
      participant: session.participant,
      sessionId: session.sessionId,
      path: sessionPath(session.sessionId),
      method: "DELETE",
      fetchImpl
    }).catch(() => null);
    throw new Error(`Horizon could not securely persist the Wallet Link participant: ${error instanceof Error ? error.message : "storage failed"}`);
  }
  const qrPayload = {
    protocolVersion: WALLET_LINK_PROTOCOL_VERSION,
    dappId: WALLET_LINK_DAPP_ID,
    origin: KEYSTONE_WALLET_LINK_ORIGIN,
    redirectUri: KEYSTONE_WALLET_LINK_REDIRECT_URI,
    displayName: "Synergy Horizon",
    publicSessionId: sessionId,
    walletCapability,
    topic: session.topic,
    nonce: session.nonce,
    expiresAt,
    dappPublicKey: session.participant.signingPublicKey,
    dappTransportPublicKey: session.participant.transportPublicKey,
    state: session.state,
    codeChallenge: session.codeChallenge,
    codeChallengeMethod: "S256",
    requestedScopes: [...session.requestedScopes],
    requestedChains: [...session.requestedChains],
    requestedMethods: [...session.requestedMethods],
    requestedEvents: [...session.requestedEvents],
    requestedDurationSeconds: session.requestedDurationSeconds
  };
  return { ...session, walletCapability, qrPayload, deepLink: buildWalletPairingDeepLink(qrPayload) };
}

export async function resumeWalletLinkAuthorization(
  callback,
  {
    fetchImpl,
    loadAuthorization = loadWalletLinkAuthorization,
    decryptVerifier = decryptWalletLinkAuthorizationVerifier,
    removeAuthorization = removeWalletLinkAuthorization,
    persistSession
  } = {}
) {
  const record = await loadAuthorization();
  if (!record) throw new Error("Horizon does not have a pending Wallet Web Hub authorization.");
  try {
    const codeVerifier = await decryptVerifier(record);
    return await completeWalletLinkAuthorization(restoredAuthorizationFlow(record, codeVerifier), callback, { fetchImpl, persistSession });
  } finally {
    await removeAuthorization().catch(() => null);
  }
}

export function captureWalletLinkCallback(locationLike = globalThis.location, historyLike = globalThis.history) {
  if (String(locationLike?.pathname || "") !== "/wallet/callback") return { isCallback: false };
  const original = new URL(locationLike.href);
  historyLike.replaceState(null, "", "/wallet/callback");
  const unknown = [...original.searchParams.keys()].filter((key) => !["code", "state"].includes(key));
  const codeValues = original.searchParams.getAll("code");
  const stateValues = original.searchParams.getAll("state");
  const code = String(codeValues[0] || "");
  const state = String(stateValues[0] || "");
  if (
    original.origin + original.pathname !== KEYSTONE_WALLET_LINK_REDIRECT_URI ||
    original.hash ||
    unknown.length ||
    codeValues.length !== 1 ||
    stateValues.length !== 1 ||
    !AUTHORIZATION_CODE_PATTERN.test(code) ||
    !STATE_PATTERN.test(state)
  ) return { isCallback: true, error: "Wallet Web Hub returned an invalid Horizon callback." };
  return { isCallback: true, callback: { code, state } };
}

export async function restoreWalletLinkBrowserSession({
  loadSession = loadWalletLinkSession,
  removeSession = removeWalletLinkSession
} = {}) {
  const record = await loadSession();
  if (!record) return null;
  if (
    record.recordVersion !== 1 ||
    record.protocolVersion !== 2 ||
    record.dappId !== WALLET_LINK_DAPP_ID ||
    record.origin !== KEYSTONE_WALLET_LINK_ORIGIN ||
    record.redirectUri !== KEYSTONE_WALLET_LINK_REDIRECT_URI ||
    !SESSION_ID_PATTERN.test(record.sessionId) ||
    Date.parse(record.expiresAt) <= Date.now() ||
    record.signingPrivateKey.extractable ||
    record.transportPrivateKey.extractable
  ) {
    await removeSession().catch(() => null);
    return null;
  }
  return {
    protocolVersion: WALLET_LINK_PROTOCOL_VERSION,
    dappId: WALLET_LINK_DAPP_ID,
    origin: KEYSTONE_WALLET_LINK_ORIGIN,
    redirectUri: KEYSTONE_WALLET_LINK_REDIRECT_URI,
    sessionId: record.sessionId,
    topic: record.topic,
    nonce: record.nonce,
    state: record.state,
    codeChallenge: record.codeChallenge,
    participant: storedWalletLinkParticipant(record),
    expiresAt: record.expiresAt,
    requestedScopes: [...record.requestedScopes],
    requestedChains: [...record.requestedChains],
    requestedMethods: [...record.requestedMethods],
    requestedEvents: [...record.requestedEvents],
    requestedDurationSeconds: record.requestedDurationSeconds,
    walletPublicKey: record.walletPublicKey,
    walletTransportPublicKey: record.walletTransportPublicKey
  };
}

export function normalizeWalletApproval(payload, session) {
  const source = payload?.session && typeof payload.session === "object" ? { ...payload, ...payload.session } : payload;
  const status = String(source?.status || source?.lifecycleState || "pending").toLowerCase();
  if (["revoked", "rejected", "denied", "cancelled", "canceled"].includes(status)) {
    return { status: "rejected", connected: false, error: "Connection was rejected in Synergy Wallet." };
  }
  if (status === "expired") return { status: "expired", connected: false, error: "Synergy Wallet connection request expired." };
  if (!["approved", "active"].includes(status)) {
    return { status: "pending", connected: false, expiresAt: source?.expiresAt || session.expiresAt };
  }
  try {
    if (
      Number(source.protocolVersion) !== WALLET_LINK_PROTOCOL_VERSION ||
      source.sessionId !== session.sessionId ||
      source.dappId !== WALLET_LINK_DAPP_ID ||
      source.origin !== KEYSTONE_WALLET_LINK_ORIGIN ||
      source.redirectUri !== KEYSTONE_WALLET_LINK_REDIRECT_URI
    ) throw new Error("Wallet Link approval is bound to a different Horizon session.");
    if (source.proofVerified !== true || source.sessionBindingVerified !== true) {
      throw new Error("Wallet Connection Service did not verify the wallet approval binding.");
    }
    const proof = source.approvalProof;
    const binding = proof?.signedPayload;
    const address = extractAccount(source).toLowerCase();
    if (!binding || !proof?.publicKeyHex || !address) throw new Error("Wallet Link approval is missing its verified wallet binding.");
    const exact = {
      type: "synergy-wallet-link-connection-approval",
      version: 2,
      sessionId: session.sessionId,
      nonce: session.nonce,
      dappId: WALLET_LINK_DAPP_ID,
      origin: KEYSTONE_WALLET_LINK_ORIGIN,
      redirectUri: KEYSTONE_WALLET_LINK_REDIRECT_URI,
      state: session.state,
      codeChallenge: session.codeChallenge,
      codeChallengeMethod: "S256",
      walletAddress: address,
      dappPublicKey: session.participant.signingPublicKey,
      dappTransportPublicKey: session.participant.transportPublicKey,
      requestedDurationSeconds: session.requestedDurationSeconds
    };
    for (const [key, expected] of Object.entries(exact)) {
      if (binding[key] !== expected) throw new Error(`Wallet approval ${key} binding mismatch.`);
    }
    if (!isWalletLinkTestnet(source.chainId) || !isWalletLinkTestnet(binding.chainId)) {
      return {
        status: "network-mismatch",
        connected: false,
        account: address,
        chainId: walletLinkChainIdToDecimal(source.chainId),
        error: "Synergy Wallet approval is not bound to Synergy Testnet v3."
      };
    }
    assertExactStringSet(binding.scopes, session.requestedScopes, "approved scope");
    assertExactStringSet(binding.chains, session.requestedChains, "approved chain");
    assertExactStringSet(binding.methods, session.requestedMethods, "approved method");
    assertExactStringSet(binding.events, session.requestedEvents, "approved event");
    const walletTransportPublicKey = String(source.transport?.walletPublicKey || binding.walletTransportPublicKey || "");
    if (!walletTransportPublicKey || source.transport?.dappPublicKey !== session.participant.transportPublicKey) {
      throw new Error("Wallet Link approval is missing its wallet transport binding.");
    }
    const expiresAt = requireFutureExpiry(source.expiresAt || binding.sessionExpiresAt);
    const approvedSession = {
      ...session,
      expiresAt,
      walletAddress: address,
      walletPublicKey: String(proof.publicKeyHex),
      walletTransportPublicKey
    };
    return {
      status: "approved",
      connected: true,
      account: address,
      address,
      chainId: SYNERGY_TESTNET.chainIdDecimal,
      chainIdHex: SYNERGY_TESTNET.chainIdHex,
      synId: source.synId || binding.synId || null,
      source: "wallet-link-v2",
      canSign: true,
      expiresAt,
      session: approvedSession
    };
  } catch (error) {
    return { status: "error", connected: false, error: error instanceof Error ? error.message : "Wallet Link approval is invalid." };
  }
}

export async function pollWalletPairingSession(session, { fetchImpl, persistSession = saveWalletLinkSession } = {}) {
  const payload = await walletLinkRequest({
    participant: session.participant,
    sessionId: session.sessionId,
    path: sessionPath(session.sessionId),
    fetchImpl
  });
  const result = normalizeWalletApproval(payload, session);
  if (result.connected && result.session) await persistSession(storedSession(result.session));
  return result;
}

function normalizeWalletLinkAction(payload, session, expected = null) {
  const action = payload?.request || payload;
  const requestId = String(action?.requestId || action?.id || "");
  if (
    !ACTION_ID_PATTERN.test(requestId) ||
    action?.sessionId !== session.sessionId ||
    action?.dappId !== WALLET_LINK_DAPP_ID ||
    action?.origin !== session.origin ||
    !session.requestedMethods.includes(action?.method)
  ) throw new Error("Wallet Link action response did not match the Horizon session request.");
  if (expected) {
    for (const key of ["account", "chain", "expiresAt", "idempotencyKey", "actionBodyHash", "method"]) {
      if (expected[key] !== undefined && action[key] !== expected[key]) {
        throw new Error(`Wallet Link changed the action ${key} binding.`);
      }
    }
    if (expected.encryptedPayload && canonicalJson(action.encryptedPayload) !== canonicalJson(expected.encryptedPayload)) {
      throw new Error("Wallet Link changed the encrypted action payload.");
    }
  }
  return {
    requestId,
    sessionId: action.sessionId,
    dappId: action.dappId,
    origin: action.origin,
    method: action.method,
    account: String(action.account || ""),
    chain: String(action.chain || ""),
    status: String(action.status || "").toLowerCase(),
    expiresAt: String(action.expiresAt || ""),
    actionBodyHash: String(action.actionBodyHash || ""),
    idempotencyKey: String(action.idempotencyKey || ""),
    encryptedPayload: action.encryptedPayload,
    encryptedResult: action.encryptedResult || null,
    resultProofBinding: action.resultProofBinding || null,
    resultVerified: action.resultVerified === true,
    reason: action.reasonCode || action.reason || null
  };
}

export async function createWalletLinkTransactionAction({ session, method, envelope, account, kind, fetchImpl }) {
  if (!["synergy_signTransaction", "synergy_sendTransaction"].includes(method) || !session.requestedMethods.includes(method)) {
    throw new Error("The Wallet Link session was not granted the requested transaction action.");
  }
  if (!session.walletTransportPublicKey || !session.walletPublicKey) {
    throw new Error("Wallet action requires the approved wallet transport binding.");
  }
  const normalizedAccount = String(account || "").toLowerCase();
  const chain = "synergy:1266";
  const idempotencyKey = `horizon-${randomBase64Url(24)}`;
  const expiresAt = new Date(Date.now() + 5 * 60_000).toISOString();
  const senderKeyFingerprint = await walletLinkPublicKeyFingerprint(session.participant.transportPublicKey);
  const recipientKeyFingerprint = await walletLinkPublicKeyFingerprint(session.walletTransportPublicKey);
  const aad = {
    type: "synergy-wallet-link-action-request-aad",
    version: 2,
    sessionId: session.sessionId,
    dappId: session.dappId,
    origin: session.origin,
    method,
    chain,
    account: normalizedAccount,
    idempotencyKey,
    expiresAt,
    senderKeyFingerprint,
    recipientKeyFingerprint
  };
  const encryptedPayload = await encryptWalletLinkEnvelope({
    participant: session.participant,
    plaintext: {
      type: "synergy-wallet-link-encrypted-action",
      version: 2,
      method,
      params: [envelope],
      label: method === "synergy_signTransaction" ? `Sign Keystone ${kind}` : `Submit Keystone ${kind}`,
      summary: `Approve this ${kind} governance transaction for Synergy Keystone.`,
      metadata: { purpose: "keystone-governance-transaction", governanceKind: kind, origin: session.origin }
    },
    aad,
    peerPublicKey: session.walletTransportPublicKey,
    senderKeyFingerprint,
    recipientKeyFingerprint
  });
  const body = { account: normalizedAccount, chain, encryptedPayload, expiresAt, idempotencyKey, method };
  const bodyText = canonicalJson(body);
  const payload = await walletLinkRequest({
    participant: session.participant,
    sessionId: session.sessionId,
    path: `${sessionPath(session.sessionId)}/requests`,
    method: "POST",
    body,
    fetchImpl
  });
  return normalizeWalletLinkAction(payload, session, {
    ...body,
    actionBodyHash: await sha256Base64Url(bodyText)
  });
}

export async function createWalletLinkMessageAction({ session, payload, account, fetchImpl }) {
  const method = "synergy_signMessage";
  if (!session?.requestedMethods?.includes(method)) {
    throw new Error("The Wallet Link session was not granted message signing.");
  }
  if (!session.walletTransportPublicKey || !session.walletPublicKey) {
    throw new Error("Wallet message signing requires the approved wallet transport binding.");
  }
  const normalizedAccount = String(account || "").trim().toLowerCase();
  if (!normalizedAccount || normalizedAccount !== String(session.walletAddress || normalizedAccount).toLowerCase()) {
    throw new Error("Wallet message signing account is invalid.");
  }
  const chain = "synergy:1266";
  const idempotencyKey = `horizon-${randomBase64Url(24)}`;
  const expiresAt = new Date(Date.now() + 5 * 60_000).toISOString();
  const senderKeyFingerprint = await walletLinkPublicKeyFingerprint(session.participant.transportPublicKey);
  const recipientKeyFingerprint = await walletLinkPublicKeyFingerprint(session.walletTransportPublicKey);
  const aad = {
    type: "synergy-wallet-link-action-request-aad",
    version: 2,
    sessionId: session.sessionId,
    dappId: session.dappId,
    origin: session.origin,
    method,
    chain,
    account: normalizedAccount,
    idempotencyKey,
    expiresAt,
    senderKeyFingerprint,
    recipientKeyFingerprint,
  };
  const encryptedPayload = await encryptWalletLinkEnvelope({
    participant: session.participant,
    plaintext: {
      type: "synergy-wallet-link-encrypted-action",
      version: 2,
      method,
      params: [payload, normalizedAccount],
      label: "Authorize Synergy Horizon",
      summary: "Sign Horizon's one-time authentication challenge. This does not submit a transaction.",
      metadata: { purpose: "horizon-wallet-auth", origin: session.origin },
    },
    aad,
    peerPublicKey: session.walletTransportPublicKey,
    senderKeyFingerprint,
    recipientKeyFingerprint,
  });
  const body = { account: normalizedAccount, chain, encryptedPayload, expiresAt, idempotencyKey, method };
  const bodyText = canonicalJson(body);
  const response = await walletLinkRequest({
    participant: session.participant,
    sessionId: session.sessionId,
    path: `${sessionPath(session.sessionId)}/requests`,
    method: "POST",
    body,
    fetchImpl,
  });
  return normalizeWalletLinkAction(response, session, { ...body, actionBodyHash: await sha256Base64Url(bodyText) });
}

export async function pollWalletLinkAction(session, requestId, { fetchImpl } = {}) {
  if (!ACTION_ID_PATTERN.test(requestId)) throw new Error("Wallet Link action ID is invalid.");
  const payload = await walletLinkRequest({
    participant: session.participant,
    sessionId: session.sessionId,
    path: `${sessionPath(session.sessionId)}/requests/${encodeURIComponent(requestId)}`,
    fetchImpl
  });
  return normalizeWalletLinkAction(payload, session);
}

async function decryptWalletLinkActionResult(session, action) {
  const binding = action.resultProofBinding;
  if (!action.resultVerified || !binding || !action.encryptedResult || !session.walletTransportPublicKey || !session.walletPublicKey) {
    throw new Error("Wallet Connection Service did not verify the encrypted Horizon action result.");
  }
  const exact = {
    requestId: action.requestId,
    sessionId: session.sessionId,
    dappId: session.dappId,
    origin: session.origin,
    walletAddress: action.account,
    chainId: action.chain,
    walletPublicKey: session.walletPublicKey,
    actionBodyHash: action.actionBodyHash,
    encryptedResultHash: await sha256Base64Url(canonicalJson(action.encryptedResult))
  };
  for (const [key, expected] of Object.entries(exact)) {
    if (binding[key] !== expected) throw new Error(`Wallet action result ${key} binding is invalid.`);
  }
  const senderKeyFingerprint = await walletLinkPublicKeyFingerprint(session.walletTransportPublicKey);
  const recipientKeyFingerprint = await walletLinkPublicKeyFingerprint(session.participant.transportPublicKey);
  const aad = {
    type: "synergy-wallet-link-action-result-aad",
    version: 2,
    sessionId: session.sessionId,
    dappId: session.dappId,
    origin: session.origin,
    requestId: action.requestId,
    method: action.method,
    chain: action.chain,
    account: action.account,
    actionBodyHash: action.actionBodyHash,
    actionExpiresAt: action.expiresAt,
    senderKeyFingerprint,
    recipientKeyFingerprint
  };
  return decryptWalletLinkEnvelope({
    participant: session.participant,
    envelope: action.encryptedResult,
    aad,
    peerPublicKey: session.walletTransportPublicKey,
    senderKeyFingerprint,
    recipientKeyFingerprint
  });
}

export async function waitForWalletLinkAction(
  session,
  requestId,
  { fetchImpl, intervalMs = 1_800, timeoutMs = 5 * 60_000, signal } = {}
) {
  const startedAt = Date.now();
  while (Date.now() - startedAt < Math.max(1, timeoutMs)) {
    if (signal?.aborted) throw new DOMException("Wallet action wait was cancelled.", "AbortError");
    const action = await pollWalletLinkAction(session, requestId, { fetchImpl });
    if (action.status === "completed") return { ...action, result: await decryptWalletLinkActionResult(session, action) };
    if (["rejected", "expired", "failed"].includes(action.status)) {
      throw new Error(action.reason || `Synergy Wallet ${action.status} the Keystone transaction request.`);
    }
    await new Promise((resolve, reject) => {
      const timer = globalThis.setTimeout(resolve, Math.max(0, intervalMs));
      signal?.addEventListener("abort", () => {
        globalThis.clearTimeout(timer);
        reject(new DOMException("Wallet action wait was cancelled.", "AbortError"));
      }, { once: true });
    });
  }
  throw new Error("Synergy Wallet action approval timed out.");
}

export async function revokeWalletLinkSession(
  session,
  { fetchImpl, removeSession = removeWalletLinkSession } = {}
) {
  try {
    await walletLinkRequest({
      participant: session.participant,
      sessionId: session.sessionId,
      path: sessionPath(session.sessionId),
      method: "DELETE",
      fetchImpl
    });
  } finally {
    await removeSession().catch(() => null);
  }
}

export const KEYSTONE_WALLET_LINK_QR_OPTIONS = Object.freeze({
  errorCorrectionLevel: "L",
  margin: 4,
  width: 740,
  color: Object.freeze({ dark: "#090414", light: "#f7f4ff" })
});

export function qrToDataUrl(payload) {
  return QRCode.toDataURL(canonicalJson(payload), KEYSTONE_WALLET_LINK_QR_OPTIONS);
}
